import { cloneDeep } from "lodash";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import {
  Plus,
  Check,
  Pencil,
  RotateCw,
  Trash,
  CircleAlert,
  X,
} from "lucide-react";

import { Button } from "@/design-system/components/Button/Button";
import {
  Dialog,
  DialogActionsWrapper,
} from "@/design-system/components/Dialog/Dialog";

import { EditableEnvironment, EditableProject } from "../reducers/environments";

export type ModalState = {
  title: string;
  placeholder: string;
  project: EditableProject;
  closeOnOverlay: boolean;
  addingNewProject?: boolean;
  error?: string;
  addingNewEnvironment?: boolean;
  onClose?: () => void;
  onSave: (updatedProject: EditableProject) => void;
  isLoading: boolean;
};

const EditProjectModalEnvironmentRow: React.FC<{
  environment: EditableEnvironment;
  updateEnvironmentName: (name: string) => void;
  removeEnvironment: () => void;
}> = ({ removeEnvironment, updateEnvironmentName, environment }) => {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    removeEnvironment();
    setIsEditing(false);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [inputRef, isEditing]);

  return (
    <div className="flex items-center justify-between rounded px-2 py-1 hover:bg-gray-50">
      {!isEditing ? (
        <>
          <button
            type="button"
            tabIndex={0}
            className="cursor-text py-1 text-sm text-gray-800"
            onMouseDown={(e) => {
              e.preventDefault();
              startEditing();
            }}
          >
            {environment.name || (
              <span className="text-red-400">Missing Environment Name...</span>
            )}
          </button>
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="cursor-pointer rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
              onMouseDown={(e) => {
                e.preventDefault();
                startEditing();
              }}
            >
              <Pencil size={16} />
            </button>
            {environment.deletable && (
              <button
                type="button"
                className="cursor-pointer rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-700"
                onClick={handleDelete}
              >
                <Trash size={16} />
              </button>
            )}
          </div>
        </>
      ) : (
        <input
          placeholder="Environment name..."
          tabIndex={0}
          onBlur={cancelEditing}
          ref={inputRef}
          value={environment.name}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            updateEnvironmentName(e.target.value)
          }
          className="w-full rounded border bg-gray-50 p-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
        />
      )}
    </div>
  );
};

export const EditProjectModal: React.FC<ModalState> = ({
  addingNewEnvironment = false,
  addingNewProject = false,
  project,
  placeholder,
  onSave,
  title,
  onClose,
  error,
  isLoading,
}) => {
  const [addingNewEnv, setAddingNewEnv] = useState(addingNewEnvironment);
  const [newEnvironmentName, setNewEnvironmentName] = useState("");
  const addingNewRef = useRef<HTMLInputElement>(null);
  const projectNameRef = useRef<HTMLInputElement>(null);
  const [newProjectState, setNewProjectState] = useState(cloneDeep(project));

  const onConfirm = () => {
    onSave(newProjectState);
  };

  const saveNewEnv = () => {
    if (!newEnvironmentName.trim()) return;
    const envsCopy = [...newProjectState.environments];
    envsCopy.push({
      accountID: newProjectState.accountID,
      createdAt: null,
      projectID: newProjectState.ID,
      ID: null,
      name: newEnvironmentName.trim(),
      deletable: true,
      config: {},
    });
    setNewProjectState({ ...newProjectState, environments: envsCopy });
    setNewEnvironmentName("");
    setAddingNewEnv(false);
  };

  const cancelAddingNewEnv = () => {
    setNewEnvironmentName("");
    setAddingNewEnv(false);
  };

  useEffect(() => {
    setTimeout(() => {
      if (addingNewEnv && addingNewRef.current) {
        addingNewRef.current.focus();
      } else if (addingNewProject && projectNameRef.current) {
        projectNameRef.current.focus();
      }
    }, 1);
  }, [addingNewEnv, addingNewProject]);

  const updateEnvironmentName = (envIndex: number, name: string) => {
    const envsCopy = cloneDeep(newProjectState.environments);
    envsCopy[envIndex].name = name;
    setNewProjectState({ ...newProjectState, environments: envsCopy });
  };

  const removeEnvironment = (envIndex: number) => {
    if (newProjectState.environments[envIndex].deletable) {
      const envsCopy = [...newProjectState.environments];
      envsCopy.splice(envIndex, 1);
      setNewProjectState({ ...newProjectState, environments: envsCopy });
    } else {
      toast.error("You can not delete this environment");
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose?.();
  };

  return (
    <Dialog
      open={true}
      onOpenChange={handleOpenChange}
      title={title}
      contentProps={{ className: "w-[500px]" }}
    >
      <div className="flex flex-col gap-6">
        <div>
          <label className="text-sm font-medium text-gray-700">Name</label>
          <input
            tabIndex={0}
            className="mt-1 w-full rounded border bg-white p-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none dark:bg-gray-100"
            placeholder={`${placeholder} name...`}
            type="text"
            ref={projectNameRef}
            value={newProjectState.name}
            onChange={(e) =>
              setNewProjectState({ ...newProjectState, name: e.target.value })
            }
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">
            Environments
          </label>
          <div className="mt-1 divide-y rounded border">
            {newProjectState.environments.map(
              (e: EditableEnvironment, index: number) => (
                <EditProjectModalEnvironmentRow
                  removeEnvironment={() => removeEnvironment(index)}
                  updateEnvironmentName={(name) =>
                    updateEnvironmentName(index, name)
                  }
                  key={index}
                  environment={e}
                />
              ),
            )}

            {addingNewEnv && (
              <div className="flex items-center gap-1.5 p-2">
                <input
                  tabIndex={0}
                  placeholder="Environment name..."
                  ref={addingNewRef}
                  value={newEnvironmentName}
                  onChange={(e) => setNewEnvironmentName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveNewEnv();
                    if (e.key === "Escape") cancelAddingNewEnv();
                  }}
                  className="min-w-0 flex-1 rounded border bg-gray-50 p-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={saveNewEnv}
                  disabled={!newEnvironmentName.trim()}
                  className="cursor-pointer rounded bg-blue-600 p-1.5 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Check size={16} />
                </button>
                <button
                  type="button"
                  onClick={cancelAddingNewEnv}
                  className="cursor-pointer rounded p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {!addingNewEnv && (
            <button
              type="button"
              tabIndex={0}
              onClick={() => setAddingNewEnv(true)}
              className="mt-2 flex cursor-pointer items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
            >
              <Plus size={16} />
              Add Environment
            </button>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded bg-red-500 px-3 py-2 text-sm font-bold text-white">
            <CircleAlert className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center py-2">
            <RotateCw className="h-5 w-5 animate-spin text-gray-500" />
          </div>
        )}

        <DialogActionsWrapper>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isLoading}>
            Save
          </Button>
        </DialogActionsWrapper>
      </div>
    </Dialog>
  );
};
